<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-[450px]">
      <DialogHeader>
        <DialogTitle>Rename key</DialogTitle>
      </DialogHeader>
      <div class="px-7 pb-6 pt-5 w-full md:min-w-[400px]">
        <form id="update-token" class="space-y-5" @submit="onSubmit">
          <fieldset class="flex flex-col gap-1">
            <Label for="name">Name</Label>
            <Input id="name" v-model="name" type="text" v-bind="nameAttrs" />
          </fieldset>
        </form>
      </div>
      <DialogFooter>
        <DialogClose as-child>
          <Button type="button" variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit" form="update-token">Save</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@ui/components/dialog';
import { useForm } from 'vee-validate';
import { z } from 'zod';
import { Label } from '@ui/components/label';
import { Input } from '@ui/components/input';
import { Button } from '@ui/components/button';
import { toTypedSchema } from '@vee-validate/zod';
import { toast } from 'vue-sonner';
import requests from '@vuex/requests';
import { useStore } from 'vuex';

const props = defineProps({
  tokenId: {
    type: Number,
    default: null,
  },
  tokenName: {
    type: String,
    default: null,
  },
});
const tokenName = computed(() => props.tokenName);
const tokenId = computed(() => props.tokenId);
const open = defineModel('open', { type: Boolean, default: false });

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, { message: 'Name is required' }).max(255),
  })
);

const queryClient = useQueryClient();

const { handleSubmit, resetForm, defineField } = useForm({
  validationSchema,
  initialValues: {
    name: tokenName.value,
  },
});

const [name, nameAttrs] = defineField('name');

watch(open, () => {
  name.value = tokenName.value;
});
const store = useStore();

const { mutate } = useMutation({
  mutationFn: async payload => {
    await requests.workspaces.apiTokens.update({
      ...payload,
    });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['workspaceTokens'] });
    resetForm();
    open.value = false;
  },
  onError: err => {
    console.error(err);
    if (err?.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error('Updating the API Token was unsuccessful.');
    }
  },
});

const onSubmit = handleSubmit(values => {
  mutate({
    id: tokenId.value,
    name: values.name,
    workspaceId: store.getters['workspace/getId'],
  });
});
</script>
